// ... (imports and initial setup unchanged)
import { useEffect, useState } from "react";
import "../../styles/UserHome.css";
import "../../styles/Form.css";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import DonutFileStatusChart from "../../components/DonutFileStatusChart";

function UserHome() {
  const navigate = useNavigate();
  const { id } = useParams();
  const userId = id || localStorage.getItem("userId");
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const currentUserId = Number(localStorage.getItem("userId"));
  const isAdmin = localStorage.getItem("userRole") === "1";

  const [form, setForm] = useState({
    first_name: "",
    mid_name: "",
    last_name: "",
    email: "",
    mobile: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    dob: "",
    profilePic: "",
    userid_no: "",
    gender: "",
    sin: "",
  });

  const [selectedTraining, setSelectedTraining] = useState("");
  const [taskStatus, setTaskStatus] = useState({});
  const [categories, setCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [assignedTrainings, setAssignedTrainings] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState([]); // [{category:{id,name}, tasks:[...] }]
  const [showSin, setShowSin] = useState(false);

  const formatSin = (sin, show) => {
    if (!sin) return "-";
    if (show) return sin;

    const last4 = sin.slice(-4);
    return `*****${last4}`;
  };

  const handleTrainingSelect = (e) => {
    const moduleKey = e.target.value;
    setSelectedTraining(moduleKey);
    if (!moduleKey) return;

    // go to the Training Panel (preserves admin/user route pattern you already use)
    if (userId) {
      navigate(`/training/${userId}`, { state: { openModuleKey: moduleKey } });
    } else {
      navigate(`/training`, { state: { openModuleKey: moduleKey } });
    }
  };

  const profileImageUrl = form.profilePic
    ? `${BASE_URL}/uploads/profilepic/${form.profilePic}`
    : "/images/default-user.jpg";

  useEffect(() => {
    if (!userId) return;

    (async () => {
      const res = await axios.get(`${BASE_URL}/api/tasks/assigned/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const rows = res.data?.tasks || [];

      // Group by category
      const byCat = new Map();
      rows.forEach((r) => {
        const k = r.t_category_id;
        if (!byCat.has(k)) {
          byCat.set(k, {
            category: {
              id: r.t_category_id,
              name: r.tc_name,
              key: r.tc_key, // e.g. 'employment_record', 'compliance'
              order: r.tc_order ?? 9999,
            },
            tasks: [],
          });
        }
        byCat.get(k).tasks.push({ ...r, order: r.t_order ?? 9999 });
      });

      // 1) sort tasks within each category
      for (const g of byCat.values()) {
        g.tasks.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
      }

      // 2) force Employment Record first, Compliance second, then tc_order
      const PRIORITY = { employment_record: 0, compliance: 1 };
      const sortedGroups = Array.from(byCat.values()).sort((a, b) => {
        const pa = PRIORITY[a.category.key] ?? 100;
        const pb = PRIORITY[b.category.key] ?? 100;
        if (pa !== pb) return pa - pb;
        if ((a.category.order ?? 9999) !== (b.category.order ?? 9999)) {
          return (a.category.order ?? 9999) - (b.category.order ?? 9999);
        }
        return String(a.category.name).localeCompare(String(b.category.name));
      });

      setAssignedGroups(sortedGroups);
    })().catch(console.error);
  }, [userId, BASE_URL]);

  // 1) bring back the profile fetch so setForm is used
  useEffect(() => {
    if (!userId) return;
    axios
      .get(`${BASE_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setForm(res.data))
      .catch(() => console.error("Failed to load user profile."));
  }, [userId, BASE_URL]);

  // 2) define openTask (you call it in the onClick)
  const openTask = (task) => {
    const viewingUserId = userId; // target user

    if (task.t_key === "consent_history") {
      // ✅ go to list to choose which employer (regular flow, not slide)
      navigate(`/consent/list/${viewingUserId}`);
      return;
    }

    // your existing special case for user_information (profile vs edit)
    if (task.t_key === "user_information") {
      if (isAdmin && currentUserId !== Number(viewingUserId)) {
        navigate(`/edit-user/${viewingUserId}`);
      } else {
        navigate(`/profile`);
      }
      return;
    }

    // default
    if (!task.t_route) return;
    navigate(task.t_route.replace(":userId", viewingUserId));
  };

  const getTaskStatus = (task) => {
    const idKey = String(task.t_id);
    const keyKey = String(task.t_key || "");
    return taskStatus?.[idKey] || taskStatus?.[keyKey] || "not_completed";
  };

  const isDone = (task) => getTaskStatus(task) === "completed";
  const isReview = (task) => getTaskStatus(task) === "review";

  // Categories that don't require signing shouldn't be counted in File Status
  const EXCLUDE_KEYS = new Set(["employment_record", "compliance"]);

  // flatten assigned tasks but skip excluded categories
  const assignedTaskList = assignedGroups.flatMap((g) =>
    EXCLUDE_KEYS.has(String(g.category.key || "").toLowerCase()) ? [] : g.tasks
  );

  // DB-driven totals
  const assignedTotal = assignedTaskList.length;
  const completedCount = assignedTaskList.filter((t) => isDone(t)).length;
  const reviewCount = assignedTaskList.filter((t) => isReview(t)).length;
  const notStartedCount = Math.max(
    assignedTotal - completedCount - reviewCount,
    0
  );

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const baseStatus = res.data.status || {};

        // ✅ Fetch Vacation Form latest status (like CVSA)
        const VACATION_TASK_ID = 1; // you said vacation task id = 1
        try {
          const vacRes = await axios.get(
            `${BASE_URL}/api/vacationform/latest/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const vacStatus = vacRes.data.status;

          // set by both key and id so icon logic works regardless of how tasks are referenced
          baseStatus["vacation_form"] = vacStatus;
          baseStatus[String(VACATION_TASK_ID)] = vacStatus;
        } catch (err) {
          console.error("⚠️ Failed to fetch Vacation status", err);
        }

        // ✅ Fetch CVSA latest status
        const CVSA_TASK_ID = 36;

        try {
          const cvsaRes = await axios.get(
            `${BASE_URL}/api/cvsa/latest/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const status = cvsaRes.data.status;

          // ✅ set by both key and id
          baseStatus["cvsa_inspections"] = status;
          baseStatus[String(CVSA_TASK_ID)] = status;
        } catch (err) {
          console.error("⚠️ Failed to fetch CVSA status", err);
        }

        // ✅ Fetch Work Permit latest status (like CVSA)
        const WORK_PERMIT_TASK_ID = 40; // <-- your actual task_id for Work Permit
        try {
          const wpRes = await axios.get(
            `${BASE_URL}/api/workpermit/latest/status/${userId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const wpStatus = wpRes.data.status;

          // ✅ set both by key and ID so icons update properly
          baseStatus["work_permit"] = wpStatus;
          baseStatus[String(WORK_PERMIT_TASK_ID)] = wpStatus;
        } catch (err) {
          console.error("⚠️ Failed to fetch Work Permit status", err);
        }

        // Existing Consent History logic...
        const employersRes = await axios.get(
          `${BASE_URL}/api/work-history/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const employers = employersRes.data || [];
        const workingEmployers = employers.filter(
          (emp) => emp.wh_type === "Working"
        );
        const requiredConsentTasks = workingEmployers.map(
          (emp) => `consent_history_${emp.wh_id}`
        );
        if (requiredConsentTasks.length > 0) {
          if (
            requiredConsentTasks.every(
              (task) => baseStatus[task] === "completed"
            )
          ) {
            baseStatus["consent_history"] = "completed";
          } else if (
            requiredConsentTasks.every((task) => baseStatus[task] === "review")
          ) {
            baseStatus["consent_history"] = "review";
          } else {
            baseStatus["consent_history"] = "not_completed";
          }
        }

        setTaskStatus(baseStatus);
      } catch (error) {
        console.error("Failed to fetch task statuses or employer list", error);
      }
    };

    if (userId) {
      fetchTaskStatus();
    }
  }, [userId, BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setCategories(res.data))
      .catch((err) => console.error("❌ Failed to fetch categories:", err));
  }, [BASE_URL]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/org-units`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setOrgUnits(res.data))
      .catch((err) => console.error("❌ Failed to fetch Org Units:", err));
  }, [BASE_URL]);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/trainings/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setAssignedTrainings(res.data || []);
      } catch (e) {
        console.error("Failed to load assigned trainings", e);
      }
    };
    load();
  }, [userId, BASE_URL]);

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.c_id === parseInt(categoryId));
    return cat ? cat.c_name : categoryId; // fallback to ID if not found
  };

  const getOrgUnitName = (orgUnitId) => {
    const ou = orgUnits.find((ou) => ou.ou_id === parseInt(orgUnitId));
    return ou ? ou.ou_name : orgUnitId; // fallback to ID if not found
  };

  const fullName = [form.first_name, form.mid_name, form.last_name]
    .filter(Boolean)
    .join(" ");
  const fullAddress = [
    form.address1,
    form.address2,
    form.city,
    form.province,
    form.country,
    form.postal_code,
  ]
    .filter(Boolean)
    .join(", ");
  const formattedDOB = form.dob ? dayjs(form.dob).format("YYYY-MM-DD") : "N/A";
  const age = form.dob ? dayjs().diff(dayjs(form.dob), "year") : "N/A";

  return (
    <div className="container-fluid px-2">
      <div className="form-wrapper my-4">
        {/* ✅ Only Admins see this button */}
        {isAdmin && (
          <div className="userhome-topbar">
            <button
              className="status-history-btn d-flex align-items-center gap-2"
              onClick={() => navigate(`/status-history/${userId}`)}
            >
              <i className="fa fa-clock"></i>
              <span>Status History</span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="d-flex align-items-center p-3 bg-white rounded shadow mb-4">
          <img src={profileImageUrl} alt="User" className="user-img me-4" />
          <div>
            <h5>{fullName || "User Name"}</h5>
            <p className="mb-1">
              📞{" "}
              {form.mobile ? (
                <a href={`tel:${form.mobile}`}>{form.mobile}</a>
              ) : (
                "N/A"
              )}{" "}
              • 📧{" "}
              {form.email ? (
                <a href={`mailto:${form.email}`}>{form.email}</a>
              ) : (
                "N/A"
              )}
            </p>
            <p className="mb-0">🏠 {fullAddress || "-"}</p>
          </div>
          <div className="ms-auto text-end d-flex flex-column align-items-end gap-2">
            <div className="mt-2 text-start">
              <p className="mb-2">
                Associate | {getCategoryName(form.emp_category)} |{" "}
                {getOrgUnitName(form.org_unit)}{" "}
              </p>
              <p className="mb-2">
                <strong>Employee ID:</strong> {form.userid_no}
              </p>
              {isAdmin && (
                <p className="mb-0 d-flex align-items-center gap-2">
                  <strong>SSN:</strong>

                  <span>{formatSin(form.sin, showSin)}</span>

                  {form.sin && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowSin((prev) => !prev)}
                      style={{ padding: "2px 6px" }}
                      title={showSin ? "Hide SSN" : "Show SSN"}
                    >
                      <i
                        className={`fa ${showSin ? "fa-eye-slash" : "fa-eye"}`}
                      />
                    </button>
                  )}
                </p>
              )}
              <p className="mb-0">
                <strong>Birth Date:</strong> {formattedDOB} |{" "}
                <strong>Age:</strong> {age}
              </p>
              <p className="mb-0">
                <strong>Gender:</strong> {form.gender || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Sidebar */}
          <div className="col-md-3">
            <div className="bg-white p-3 rounded shadow mb-4">
              {/* <iframe
                width="100%"
                height="250"
                src="https://www.youtube.com/embed/AxA3hdSE2fg?autoplay=1&mute=1"
                title="Motivational video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              /> */}
              <div className="video-card">
                <video
                  src="/videos/nwt.mp4"
                  width="100%"
                  height="210"
                  controls
                  autoPlay
                  muted
                  loop
                  controlsList="nodownload nofullscreen"
                  disablePictureInPicture
                  className="video-with-controls"
                />
              </div>
            </div>
            <div className="bg-white p-3 rounded shadow mb-4">
              <h6>My Training</h6>
              <select
                className="form-select"
                value={selectedTraining}
                onChange={handleTrainingSelect}
              >
                <option value="">Choose a module to begin...</option>
                {assignedTrainings.map((t) => (
                  <option key={t.tm_id} value={t.tm_key}>
                    {t.tm_title}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-white p-3 rounded shadow mb-4">
              <h6>Self Service</h6>
              <select
                className="form-select"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) navigate(value);
                }}
              >
                <option value="">Update My...</option>
                <option value={`/driverlicense/${userId}`}>
                  Driver's Licence
                </option>
                <option value={`/directdeposit/${userId}`}>
                  Direct Deposit Authorization
                </option>
              </select>
            </div>
            <div className="bg-white p-3 rounded shadow text-center file-status-card">
              <h6 className="fs-title"> File Status</h6>

              <div className="chart-wrapper">
                <DonutFileStatusChart
                  completed={completedCount}
                  review={reviewCount}
                  notStarted={notStartedCount}
                />
              </div>

              <p className="mt-3 mb-0">
                <strong>Documents:</strong> {assignedTotal}
              </p>

              <div className="status-counts mt-1">
                <span className="badge-complete">
                  ✔️ Complete: {completedCount}
                </span>
                <span className="badge-not-started">
                  🔴 Not Started: {notStartedCount}
                </span>
                <span className="badge-review">
                  ⚠️ Ready for Review: {reviewCount}
                </span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-9">
            <div className="row g-3">
              {assignedGroups.map((group) => (
                <div className="col-md-6" key={group.category.id}>
                  <div className="bg-white p-3 rounded shadow h-100">
                    <h6 className="section-title">{group.category.name}</h6>
                    <hr className="section-divider" />
                    {group.tasks.map((t) => {
                      const hideIcons =
                        ["employment_record", "compliance"].includes(
                          String(group.category.key || "").toLowerCase()
                        ) &&
                        t.t_key !== "cvsa_inspections" &&
                        t.t_key !== "vacation_form";

                      return (
                        <div
                          key={t.t_id}
                          className="hover-item"
                          onClick={() => openTask(t)}
                        >
                          - {t.t_title}{" "}
                          {!hideIcons &&
                            (isDone(t) ? (
                              <span className="text-success ms-2">✔️</span>
                            ) : isReview(t) ? (
                              <span className="text-warning ms-2">⚠️</span>
                            ) : (
                              <span className="text-danger ms-2">🔴</span>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserHome;
